import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

/**
 * Shared Studio G4 chrome (navbar + footer).
 *
 * Navbar is always rendered in a single state: translucent-white backdrop,
 * dark text, crimson hover. Scroll-dependent transparent-over-hero behaviour
 * was removed (see HomePage.jsx — hero image bleeds under the fixed navbar
 * via `absolute inset-0`, so the opaque bar overlaying the top 80px is the
 * intended look).
 */
export function G4Navbar() {
    const [open, setOpen] = useState(false);

    const navLinks = [
        { label: "Projects", href: "/#portfolio" },
        { label: "Contact", href: "/#quote" },
    ];

    return (
        <nav
            data-testid="g4-navbar"
            className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-sm"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
                <Link to="/" data-testid="g4-logo-link" className="flex items-center">
                    <img
                        src="/Assets/Studio-G4-Logo-Light-Boxed.png"
                        alt="Studio G4"
                        className="h-12 w-auto"
                    />
                </Link>
                <div className="hidden md:flex items-center gap-10">
                    {navLinks.map((l) => (
                        <a
                            key={l.label}
                            href={l.href}
                            data-testid={`g4-nav-${l.label.toLowerCase()}`}
                            className="text-sm tracking-wide font-medium transition-colors duration-150 text-white hover:text-[#650505]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <Link
                        to="/portal"
                        data-testid="g4-nav-client-portal"
                        className="text-sm tracking-wide font-medium transition-colors duration-150 text-white hover:text-[#650505]"
                    >
                        Client Portal
                    </Link>
                </div>
                <button
                    data-testid="g4-mobile-menu-toggle"
                    className="md:hidden"
                    onClick={() => setOpen(!open)}
                >
                    {open ? <X className="text-white" /> : <Menu className="text-white" />}
                </button>
            </div>
            {open && (
                <div className="md:hidden bg-white border-t border-[#E5E5E5] px-6 py-6 space-y-4">
                    {navLinks.map((l) => (
                        <a
                            key={l.label}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            className="block text-sm tracking-wide text-[#0A0A0A] font-medium"
                        >
                            {l.label}
                        </a>
                    ))}
                    <Link
                        to="/portal"
                        onClick={() => setOpen(false)}
                        className="block text-sm tracking-wide text-[#0A0A0A] font-medium"
                    >
                        Client Portal
                    </Link>
                </div>
            )}
        </nav>
    );
}

export function G4Footer() {
    return (
        <footer data-testid="g4-footer" className="bg-[#F0F0F0] py-12">
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                    <p className="font-['Outfit'] text-xl tracking-tight font-light text-[#0A0A0A]">
                        Studio{" "}
                        <span className="font-semibold" style={{ color: "#650505" }}>
                            G4
                        </span>
                    </p>
                    <p className="text-xs text-[#525252] mt-2">
                        &copy; {new Date().getFullYear()} Studio G4. All rights reserved.
                    </p>
                </div>
                <div className="flex gap-6 text-xs tracking-[0.08em] uppercase font-medium text-[#525252]">
                    <Link
                        to="/privacy"
                        data-testid="g4-footer-privacy"
                        className="hover:text-[#0A0A0A] transition-colors"
                    >
                        Privacy
                    </Link>
                    <Link
                        to="/cookies"
                        data-testid="g4-footer-cookies"
                        className="hover:text-[#0A0A0A] transition-colors"
                    >
                        Cookies
                    </Link>
                </div>
            </div>
        </footer>
    );
}
